const TOKEN_KEY = "gd_hub_token";
const USER_KEY = "gd_hub_user";

export interface User { id: string; email: string; plan: string; }

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null) {
  if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY);
}
export function getUser(): User | null {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}
export function setUser(u: User | null) {
  if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
  else localStorage.removeItem(USER_KEY);
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) { super(message); this.status = status; }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("content-type", "application/json");
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    if (res.status === 401) { setToken(null); setUser(null); }
    throw new ApiError(res.status, data.error || `HTTP ${res.status}`);
  }
  return data as T;
}

export const api = {
  register: (email: string, password: string) =>
    request<{ token: string; user: User }>("/api/auth/register", {
      method: "POST", body: JSON.stringify({ email, password }),
    }),
  login: (email: string, password: string) =>
    request<{ token: string; user: User }>("/api/auth/login", {
      method: "POST", body: JSON.stringify({ email, password }),
    }),

  listChannels: () => request<{ channels: Channel[] }>("/api/channels"),
  createChannel: (name: string, channelSecret: string) =>
    request<{ id: string; name: string; ingest_slug: string }>("/api/channels", {
      method: "POST", body: JSON.stringify({ name, channelSecret }),
    }),
  getChannel: (id: string) => request<{ channel: Channel }>(`/api/channels/${id}`),
  updateChannel: (id: string, payload: { name?: string; channelSecret?: string }) =>
    request<{ ok: true }>(`/api/channels/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteChannel: (id: string) =>
    request<{ ok: true }>(`/api/channels/${id}`, { method: "DELETE" }),

  listWebhooks: (channelId: string) =>
    request<{ webhooks: Webhook[] }>(`/api/webhooks?channel_id=${encodeURIComponent(channelId)}`),
  createWebhook: (channelId: string, name: string, targetUrl: string) =>
    request<Webhook>("/api/webhooks", {
      method: "POST", body: JSON.stringify({ channelId, name, targetUrl }),
    }),
  updateWebhook: (id: string, payload: { name?: string; targetUrl?: string; enabled?: boolean }) =>
    request<{ ok: true }>(`/api/webhooks/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteWebhook: (id: string) =>
    request<{ ok: true }>(`/api/webhooks/${id}`, { method: "DELETE" }),

  listDeliveries: (params: { channelId?: string; webhookId?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params.channelId) q.set("channel_id", params.channelId);
    if (params.webhookId) q.set("webhook_id", params.webhookId);
    if (params.limit) q.set("limit", String(params.limit));
    return request<{ deliveries: Delivery[] }>(`/api/deliveries?${q}`);
  },
};

export interface Channel {
  id: string;
  name: string;
  ingest_slug: string;
  created_at: number;
}
export interface Webhook {
  id: string;
  channel_id: string;
  name: string;
  target_url: string;
  enabled: number;
  created_at: number;
}
export interface Delivery {
  id: string;
  webhook_id: string;
  channel_id: string;
  status: "success" | "failed" | "error";
  status_code: number | null;
  attempts: number;
  request_bytes: number | null;
  response_preview: string | null;
  error_message: string | null;
  duration_ms: number | null;
  created_at: number;
}
